import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Panel } from '@/components/Panel';
import { HeritageSeal, DahliaMark } from '@/components/BrandMark';
import { toast } from 'sonner';
import { LogOut, Copy, ShoppingBag, DollarSign, Package, Trash2 } from 'lucide-react';
import ImpersonationBanner from '@/components/ImpersonationBanner';

function Stat({ label, value, sub, icon: Icon }) {
  return (
    <Panel className="p-5">
      <div className="flex items-start justify-between">
        <div><div className="h-eyebrow text-[10px]">{label}</div>
          <div className="h-display text-3xl mt-1" style={{ color: 'var(--heritage-plum)' }}>{value}</div>
          {sub && <div className="text-xs opacity-75 mt-1" style={{ color: 'var(--heritage-plum)' }}>{sub}</div>}</div>
        <div className="rounded-xl w-10 h-10 flex items-center justify-center" style={{ background: 'rgba(75,24,56,0.06)', color: 'var(--heritage-plum)' }}><Icon size={18} /></div>
      </div>
    </Panel>
  );
}

export default function AmbassadorConsole() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [me, setMe] = useState(null);
  const [orders, setOrders] = useState([]);
  const [wardrobe, setWardrobe] = useState(null);
  const [reqLoading, setReqLoading] = useState(false);

  const load = async () => {
    try {
      const [meRes, oRes, wRes] = await Promise.all([
        api.get('/ambassador/me/v2'),
        api.get('/ambassador/me/orders'),
        api.get('/ambassador/me/wardrobe'),
      ]);
      setMe(meRes.data); setOrders(oRes.data.orders || []); setWardrobe(wRes.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load'); }
  };
  useEffect(() => { load(); }, []);

  const doLogout = () => { logout(); navigate('/'); };
  const copyRef = async () => { if (!me) return; await navigator.clipboard.writeText(me.referral_link); toast.success('Link copied'); };

  const requestPayout = async () => {
    setReqLoading(true);
    try { const { data } = await api.post('/ambassador/me/request-payout'); toast.success(`Payout requested: $${data.amount.toFixed(2)}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setReqLoading(false); }
  };

  const pickItem = async (release, product, variant) => {
    try {
      await api.post('/ambassador/me/wardrobe/pick', {
        release_id: release.id, slug: product.slug, sku: variant.sku, size: variant.size, colour: variant.colour, notes: '',
      });
      toast.success(`Picked ${product.name} (${variant.size || ''} ${variant.colour || ''})`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const removePick = async (id) => {
    if (!window.confirm('Remove this pick?')) return;
    try { await api.delete(`/ambassador/me/wardrobe/pick/${id}`); toast.success('Removed'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  if (!me) return <div className="p-8" style={{ color: 'var(--heritage-plum)' }}>Loading…</div>;
  const a = me.ambassador;
  const s = me.stats;

  const TABS = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'orders', label: `Orders (${orders.length})` },
    { id: 'wardrobe', label: 'Wardrobe' },
  ];

  return (
    <div className="min-h-screen" style={{ background: 'var(--heritage-cream)' }}>
      <ImpersonationBanner />
      <header className="border-b" style={{ borderColor: 'rgba(75,24,56,0.2)', background: 'rgba(246,240,228,0.95)' }}>
        <div className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3"><DahliaMark size={44} />
            <div><div className="h-serif text-lg tracking-[0.28em]" style={{ color: 'var(--heritage-plum)' }}>HERITAGE</div><div className="h-eyebrow mt-1">Ambassador Console</div></div>
          </Link>
          <button onClick={doLogout} className="h-btn-outline text-xs"><LogOut size={14} className="mr-2" /> Logout</button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <Panel className="p-6 flex items-center gap-4 flex-wrap">
          <HeritageSeal size={56} />
          <div className="flex-1 min-w-0"><div className="h-eyebrow">Welcome</div><div className="h-display text-3xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Kia ora, {a.name}</div></div>
          <div className="flex items-center gap-2 rounded-lg p-3" style={{ background: 'rgba(75,24,56,0.05)' }}>
            <div><div className="h-eyebrow text-[10px]">Your referral link</div>
              <div className="text-xs" style={{ color: 'var(--heritage-plum)' }}>{me.referral_link}</div></div>
            <button onClick={copyRef} className="h-btn-outline text-xs" data-testid="amb-copy-referral"><Copy size={12} /></button>
          </div>
        </Panel>

        <div className="flex flex-wrap gap-2" data-testid="amb-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className="rounded-full px-4 py-1.5 text-xs h-serif transition"
              style={{ background: tab === t.id ? 'var(--heritage-plum)' : 'transparent', color: tab === t.id ? 'var(--heritage-cream)' : 'var(--heritage-plum)', border: '1px solid var(--heritage-plum)' }}
              data-testid={`amb-tab-${t.id}`}>{t.label}</button>
          ))}
        </div>

        {tab === 'dashboard' && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat label="Total sales" value={`$${s.total_sales.toLocaleString()}`} sub={`${s.orders_count} order${s.orders_count === 1 ? '' : 's'}`} icon={ShoppingBag} />
              <Stat label="Commission earned" value={`$${s.total_commission.toFixed(2)}`} sub={`at ${a.commission_percent}% per sale`} icon={DollarSign} />
              <Stat label="Pending payout" value={`$${s.pending_payout.toFixed(2)}`} icon={DollarSign} />
              <Stat label="Paid out" value={`$${s.paid_out.toFixed(2)}`} icon={Package} />
            </div>
            <Panel className="p-6 flex items-center justify-between gap-3 flex-wrap">
              <div><div className="h-serif text-xl" style={{ color: 'var(--heritage-plum)' }}>Request your payout</div>
                <div className="text-sm opacity-80" style={{ color: 'var(--heritage-plum)' }}>Heritage will transfer <b>${s.pending_payout.toFixed(2)}</b> to your account on file.</div></div>
              <button onClick={requestPayout} disabled={reqLoading || s.pending_payout <= 0} className="h-btn text-sm" data-testid="amb-request-payout">{reqLoading ? 'Requesting…' : 'Request payout'}</button>
            </Panel>
          </div>
        )}

        {tab === 'orders' && (
          <Panel className="p-4">
            {orders.length === 0 && <div className="text-center py-10 opacity-70" style={{ color: 'var(--heritage-plum)' }}>No orders through your code yet. Share your link!</div>}
            <ul className="divide-y" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
              {orders.map((o) => (
                <li key={o.id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`amb-order-${o.invoice_number}`}>
                  <div><div className="h-serif" style={{ color: 'var(--heritage-plum)' }}>{o.invoice_number}</div>
                    <div className="text-xs opacity-75" style={{ color: 'var(--heritage-plum)' }}>{new Date(o.created_at).toLocaleString('en-NZ')} · {(o.items || []).length} items · {o.status}</div></div>
                  <div className="text-right"><div className="h-serif text-lg" style={{ color: 'var(--heritage-plum)' }}>${Number(o.total).toFixed(2)}</div>
                    <div className="text-xs" style={{ color: '#5B6E2C' }}>+${Number(o.commission_amount || 0).toFixed(2)} commission</div></div>
                </li>
              ))}
            </ul>
          </Panel>
        )}

        {tab === 'wardrobe' && wardrobe && (
          <div className="space-y-5">
            {wardrobe.releases.length === 0 && <Panel className="p-8 text-center"><div className="h-serif text-xl" style={{ color: 'var(--heritage-plum)' }}>No active releases</div><div className="text-sm opacity-75 mt-2" style={{ color: 'var(--heritage-plum)' }}>Heritage will open the next wardrobe release soon.</div></Panel>}
            {wardrobe.releases.map((r) => {
              const myPicks = (wardrobe.picks || []).filter((p) => p.release_id === r.id);
              const remaining = r.max_picks - myPicks.length;
              return (
                <Panel key={r.id} className="p-5" data-testid={`amb-release-${r.id}`}>
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div><div className="h-serif text-2xl" style={{ color: 'var(--heritage-plum)' }}>{r.name}</div>
                      {r.description && <p className="text-sm opacity-85 mt-1" style={{ color: 'var(--heritage-plum)' }}>{r.description}</p>}</div>
                    <div className="h-chip text-[10px]" style={{ background: 'rgba(75,24,56,0.08)', borderColor: 'transparent', color: 'var(--heritage-plum)' }}>{myPicks.length} of {r.max_picks} picked</div>
                  </div>
                  {myPicks.length > 0 && (
                    <div className="mt-4 space-y-1.5">
                      <div className="h-eyebrow text-[10px]">Your picks</div>
                      {myPicks.map((p) => (
                        <div key={p.id} className="flex items-center justify-between gap-2 rounded-md p-2" style={{ background: 'rgba(91,110,44,0.08)' }}>
                          <div className="text-sm" style={{ color: 'var(--heritage-plum)' }}><b>{p.slug}</b> — {[p.size && `Size ${p.size}`, p.colour].filter(Boolean).join(' · ')} <span className="opacity-70">({p.status})</span></div>
                          {p.status !== 'dispatched' && <button onClick={() => removePick(p.id)} className="text-xs" style={{ color: '#9A2C2C' }}><Trash2 size={12} /></button>}
                        </div>
                      ))}
                    </div>
                  )}
                  {remaining > 0 && (
                    <div className="mt-4">
                      <div className="h-eyebrow text-[10px] mb-2">Pick from these products — {remaining} left</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {(r.products || []).map((p) => (
                          <div key={p.slug} className="rounded-lg p-3 border" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
                            <div className="flex gap-3">
                              <img src={p.image_url} alt={p.name} className="w-16 h-20 object-cover rounded flex-shrink-0" style={{ background: '#FBF8F1' }} />
                              <div className="flex-1 min-w-0"><div className="h-serif text-sm" style={{ color: 'var(--heritage-plum)' }}>{p.name}</div>
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {(p.variants || []).filter((v) => (v.stock || 0) > 0).map((v) => (
                                    <button key={v.sku} onClick={() => pickItem(r, p, v)} className="rounded-full text-[10px] h-serif px-2 py-1 border transition"
                                      style={{ borderColor: 'rgba(75,24,56,0.3)', color: 'var(--heritage-plum)', background: 'transparent' }}
                                      data-testid={`amb-pick-${p.slug}-${v.sku}`}>
                                      {[v.size, v.colour].filter(Boolean).join(' / ')}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Panel>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
